import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BillingOverviewRow {
  contractId: string
  contractNumber: string
  contractName: string | null
  customerName: string | null
  productName: string | null
  itemsCount: number
  totalCents: number
  currency: string
}

export interface BillingOverviewResult {
  periodStart: string
  periodEnd: string
  totalCents: number
  contractsCount: number
  itemsCount: number
  currency: string
  rows: BillingOverviewRow[]
}

export interface MrrByGroup {
  group: string
  groupLabel: string
  mrrCents: number
  contractsCount: number
}

export interface MrrResult {
  totalMrrCents: number
  totalArrCents: number
  contractsActive: number
  byProduct: MrrByGroup[]
  byTariff: MrrByGroup[]
  currency: string
}

export interface MarginRow {
  contractId: string
  contractNumber: string
  contractName: string | null
  customerName: string | null
  productName: string | null
  monthlyRevenueCents: number
  monthlyCostCents: number
  monthlyMarginCents: number
  marginPercent: number | null
  currency: string
}

export interface MarginResult {
  totalRevenueCents: number
  totalCostCents: number
  totalMarginCents: number
  averageMarginPercent: number | null
  currency: string
  rows: MarginRow[]
}

export type ExpiryBucket = '0_3' | '3_6' | '6_12' | 'over_12' | 'no_end'

export interface ExpiryRow {
  contractId: string
  contractNumber: string
  contractName: string | null
  customerName: string | null
  startDate: string
  endDate: string | null
  noticePeriodDays: number
  autoRenew: boolean
  daysUntilEnd: number | null
  bucket: ExpiryBucket
}

export interface ExpiryResult {
  asOf: string
  bucketCounts: Record<ExpiryBucket, number>
  rows: ExpiryRow[]
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useBillingOverview(year: number) {
  return useQuery<BillingOverviewResult>({
    queryKey: ['reports', 'billing-overview', year],
    queryFn: () =>
      apiClient.get('/reports/billing-overview', { params: { year } }).then((r) => r.data),
  })
}

export function useMrrReport() {
  return useQuery<MrrResult>({
    queryKey: ['reports', 'mrr'],
    queryFn: () => apiClient.get('/reports/mrr').then((r) => r.data),
  })
}

export function useMarginReport() {
  return useQuery<MarginResult>({
    queryKey: ['reports', 'margin'],
    queryFn: () => apiClient.get('/reports/margin').then((r) => r.data),
  })
}

export function useExpiryReport() {
  return useQuery<ExpiryResult>({
    queryKey: ['reports', 'expiry'],
    queryFn: () => apiClient.get('/reports/expiry').then((r) => r.data),
  })
}

// ─── CSV Download ───────────────────────────────────────────────────────────

export function reportCsvUrl(report: 'billing-overview' | 'mrr' | 'margin' | 'expiry', params?: Record<string, string | number>): string {
  const search = new URLSearchParams({ format: 'csv' })
  if (params) {
    for (const [k, v] of Object.entries(params)) search.append(k, String(v))
  }
  return `/api/reports/${report}?${search.toString()}`
}
